<template>
  <v-card v-if="transaction.payment">
    <suspicion-details :is-active.sync="showSuspicionInfo" :details="getSuspicionDetail" />
    <bypass-comment
      :is-active.sync="showBypassComment"
      :transaction="this.transaction"
      @commented="bypassCommented"
    />
    <rejection-comment
      :is-active.sync="showRejectionComment"
      :transaction="this.transaction"
      @commented="rejectCommented"
    />
    <send-code-form
      :is-active.sync="isSendingCode"
      :transaction="transaction.payment"
      @changed="transferCodeSent"
    />
    <v-toolbar color="grey lighten-4" light tile flat>
      <v-toolbar-title centered class="headline font-weight-bold">
        Transaction details
      </v-toolbar-title>
      <div class="flex-grow-1"></div>
      <strong>Address: {{ transaction.payment.address || "N/A" }}</strong>
      <v-spacer></v-spacer>
      <ReindexButton :paymentId="transaction.payment.id" />
      <div
        class="d-flex"
        v-if="transaction.payment.status == 'pending' && transaction.has_admin_initiator == true"
      >
        <v-btn
          color="primary"
          class="mr-3"
          @click="isSendingCode = true"
          :disabled="transaction.status !== 'initiated'"
        >
          <v-icon color="light-blue lighten-4">mdi-alert-circle-check</v-icon>
          Confirm OTP
        </v-btn>

        <v-btn
          color="primary"
          class="mr-3"
          @click="resendTransferCode"
          :disabled="transaction.status !== 'initiated'"
        >
          <v-icon color="light-blue lighten-4">mdi-alert-circle-check</v-icon>
          Resend OTP Code
        </v-btn>
      </div>

      <div v-if="transaction.started_at != null">
        <v-btn color="primary" :disabled="transaction.started_at == null" @click="downloadInvoice">
          <v-icon> mdi-download </v-icon>
          Download receipt
        </v-btn>
      </div>
      <v-spacer v-if="showStartTransferBtn"></v-spacer>

      <div v-if="transaction.payment.status == 'completed'">
        <v-btn
          v-if="showStartTransferBtn"
          color="primary"
          :loading="completingTransfer"
          :disabled="!!transactionKey('started_at', null) || processing == true"
          @click="startTransfer"
        >
          <v-icon> mdi-progress-upload </v-icon>
          Start Transfer
        </v-btn>
      </div>
      <v-spacer v-if="showStartTransferBtn"></v-spacer>
      <div v-if="transaction.payment.status == 'completed'">
        <v-btn
          v-if="showStartTransferBtn"
          color="primary"
          :loading="standingByTransfer"
          :disabled="
            !!transactionKey('started_at', null) ||
            processing == true ||
            transaction.status === 'standby'
          "
          @click="standByTransfer"
        >
          <v-icon> mdi-power-standby </v-icon>
          StandBy Transfer
        </v-btn>
      </div>
      <v-spacer v-if="showStartTransferBtn"></v-spacer>
      <div v-if="transaction.payment.status == 'completed'">
        <v-btn
          v-if="showStartTransferBtn"
          color="primary"
          :loading="pendingPaymentTransfer"
          :disabled="
            !!transactionKey('started_at', null) ||
            processing == true ||
            transaction.status === 'pending'
          "
          @click="pendingPaymentAndTransfer"
        >
          <v-icon> mdi-clock-outline </v-icon>
          Pending Payment
        </v-btn>
      </div>

      <v-btn
        color="primary"
        :loading="isArchiving"
        v-if="(
          (transaction.payment.status == 'started' && 
          transaction.payment.provider == 'BridgeApi') && 
          transaction.payment.bridge_payment_completed ||
          (transaction.payment.status == 'verified' && transaction.has_admin_initiator == true) ||
          (transaction.payment.status == 'pending' && transaction.has_admin_initiator == false)
        )
        "
        @click="completePayment"
      >
        <v-icon color="light-blue lighten-4">mdi-archive</v-icon>
        Complete Payment
      </v-btn>

      <v-spacer></v-spacer>
      <v-btn
        v-if="
          transaction.status !== 'rejected' &&
          ((transaction.payment.done_at != null && transaction.done_at == null) ||
            !!transaction.status == 'error' ||
            transaction.payment.status == 'failed')
        "
        color="primary"
        :loading="isRetrying"
        @click="retryTransfer"
        :disabled="isArchived"
      >
        <v-icon color="light-blue lighten-4">mdi-sync</v-icon>
        Retry Transfer
      </v-btn>
      <v-spacer v-if="transferIsStuck"></v-spacer>
      <v-menu open-on-hover bottom offset-y v-if="transaction.status == 'suspicious'">
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primary" dark v-bind="attrs" v-on="on">
            <v-icon color="light-blue lighten-4">mdi-sync</v-icon>
            More actions
          </v-btn>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title
              class="v-list-item--link"
              :loading="isRetryingSuspicious"
              @click="retrySuspiciousTransfer"
              :disabled="isArchived"
            >
              Bypass suspicion
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title
              v-if="transaction.status == 'suspicious' && transaction.status != 'rejected'"
              class="v-list-item--link"
              @click="rejectTransfer"
              :loading="isRejecting"
              :disabled="isArchived"
            >
              Reject
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer v-if="transferIsStuckSuspicious"></v-spacer>
      <v-btn color="primary" :loading="isArchiving" @click="toggleActivity">
        <v-icon color="light-blue lighten-4">mdi-archive</v-icon>
        Archive
      </v-btn>
      <v-btn color="primary" :loading="isRestoring" v-if="isArchived" @click="restorePayment">
        <v-icon color="light-blue lighten-4">mdi-restore</v-icon>
        Restore
      </v-btn>
    </v-toolbar>

    <!-- info -->
    <v-divider />

    <v-card-text>
      <v-row>
        <v-col cols="12" md="2">
          <h2 class="subtitle-2 text-uppercase">General</h2>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title=" Reason" :value="transactionKey('reason')"> </key-value>
        </v-col>
        <v-col cols="12" md="1">
          <key-value title=" Amount">
            <span class="font-weight-bold primary--text title">
              €{{ transaction.amount_without_fee | currency }}
            </span>
          </key-value>
        </v-col>
        <v-col cols="12" md="1">
          <key-value title="Fee">
            <span class="font-weight-bold primary--text title">
              €{{ transactionKey("applied_fee") | currency }}
            </span>
          </key-value>
        </v-col>

        <v-col cols="12" md="2">
          <key-value title="Exchange rate (with spread)">
            <span class="font-weight-bold primary--text title">
              {{ transactionKey("exchange_rate") }} ({{
                transactionKey("exchange_rate_with_spread")
              }})
            </span>
          </key-value>
        </v-col>

        <v-col cols="12" md="2">
          <key-value title="Local Amount">
            <span class="font-weight-bold primary--text title">
              {{ transactionKey("local_amount_without_fee") | currency
              }}{{ transactionKey("local_currency") }}
            </span>
          </key-value>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Danacoin Amount">
            <span class="font-weight-bold primary--text title">
              {{ transactionKey("danacoin_without_fee") | currency }} danacoins
            </span>
          </key-value>
        </v-col>
      </v-row>
    </v-card-text>
    <v-divider />
    <v-card-text>
      <v-row>
        <v-col cols="12" md="2">
          <h2 class="subtitle-2 text-uppercase">Payment Info</h2>
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Payment Id" :value="transactionKey('payment.id')" type="none" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Payment Status" :value="transactionKey('payment.status')" />
          <v-icon
            color="warning"
            v-if="transactionKey('status') == 'suspicious'"
            @click="suspicionInfo"
            >mdi-information-outline
          </v-icon>
        </v-col>

        <v-col cols="12" md="2">
          <key-value title="Type" :value="transaction.payment.type" type="title" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Provider" :value="transaction.payment.provider" />
        </v-col>
      </v-row>
    </v-card-text>

    <!--dates-->
    <v-divider />
    <v-card-text>
      <v-row>
        <v-col cols="12" md="2">
          <h2 class="subtitle-2 text-uppercase">Payment</h2>
        </v-col>
        <v-col cols="12" md="3">
          <key-value
            title="Started At"
            :value="getLocalDateTime('payment.started_at')"
            type="date"
          />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Done At" :value="getLocalDateTime('payment.done_at')" type="date" />
        </v-col>
      </v-row>
    </v-card-text>
    <!--dates-->
    <v-divider />
    <v-card-text>
      <v-row>
        <v-col cols="12" md="2">
          <h2 class="subtitle-2 text-uppercase">Transfer</h2>
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Started At" :value="getLocalDateTime('started_at')" type="date" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Done At" :value="getLocalDateTime('done_at')" type="date" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Status" :value="transactionKey('status')" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value 
            title="Auto start transfer in" 
            :value="countdownDisplay" 
            type="hm" 
          />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Status" :value="transactionKey('status')" />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script>
import sendCodeForm from "./sendCodeForm";
import moment from "moment-timezone";
import { convertUTCDateToLocalDate } from "../../store/utils.js";
import ReindexButton from "../../components/forms/ReindexButton.vue";
export default {
  components: {
    sendCodeForm,
    ReindexButton,
    SuspicionDetails: require("../payins/suspicionDetails").default,
    BypassComment: require("../payins/bypassComment").default,
    RejectionComment: require("../payins/rejectionComment").default,
  },
  props: {
    dataTransaction: {
      required: true,
    },
  },
  data() {
    return {
      transaction: this.dataTransaction,
      isShowingStartForm: false,
      isRetrying: false,
      isSendingCode: false,
      show: true,
      completingTransfer: false,
      standingByTransfer: false,
      pendingPaymentTransfer: false,
      isArchived: this.dataTransaction.isArchived || false,
      isArchiving: false,
      isRestoring: false,
      showSuspicionInfo: false,
      showBypassComment: false,
      autoStartCountdown: "00:00",
      isRejecting: false,
      showRejectionComment: false,
      processing: false,
      countDownInterval: null,
      maxWaitMinutes: 10,
      countdownSecondsLeft: 600,
      countdownDisplay: "10:00"
    };
  },
  computed: {
    getSuspicionDetail() {
      const getSuspicionKey = (data, key) => {
        let obj = {};
        for (let k of data.RegistreDetail) {
          for (let k2 of k.Valeur) {
            for (let k3 in k2) {
              obj[k3] = k2[k3];
            }
          }
        }
        if (key in obj) {
          return obj[key];
        }
      };
      let reasons = [];
      if (this.transaction.source_client.criminal_cases.length > 0) {
        let source_motif = JSON.parse(this.transaction.source_client.criminal_cases[0].data);
        let parsed_source_motif = getSuspicionKey(source_motif, "Motifs");
        let str_source_motif = `${this.transaction.source_user.full_name} : ${parsed_source_motif}`;
        reasons.push(str_source_motif);
      }
      if (this.transaction.destination_client.criminal_cases.length > 0) {
        let destination_motif = JSON.parse(
          this.transaction.destination_client.criminal_cases[0].data,
        );
        let parsed_destination_motif = getSuspicionKey(destination_motif, "Motifs");
        let str_destination_motif = `${this.transaction.destination_user.full_name} : ${parsed_destination_motif}`;
        reasons.push(str_destination_motif);
      }
      let other_reason = this.transaction.history.find(
        (it) => it.changes && it.changes.after && it.changes.after.is_suspicious,
      );
      if (other_reason) {
        reasons.push(other_reason.changes.after.reason);
      }
      return reasons.join("\n");
    },
    showStartTransferBtn() {
      return (
        this.transaction.status !== "rejected" &&
        (this.transaction.payment.provider === "SEPA" ||
          (this.transaction.payment.provider === "SEPA" &&
            this.transaction.payment.status === "completed") ||
          this.transaction.payment.type === "instant_bank_transfer" ||
          (this.transaction.payment.type === "instant_bank_transfer" &&
            this.transaction.payment.status === "completed"))
      );
    },

    transferIsStuck() {
      // the payment is currently archieved, them we shouldn't retry
      // the transfer because we may have errors retrieving the payment
      const status = this.transactionKey("status");

      return (status !== "started" && this.transaction.started_at == null) || status === "error";
    },

    transferIsStuckSuspicious() {
      // the payment is currently archieved, them we shouldn't retry
      // the transfer because we may have errors retrieving the payment
      const status = this.transactionKey("status");

      if (status === "suspicious") {
        return true;
      }
      return false;
    },
  },
  created() {
    if (this.transaction.payment.status === "completed" && 
      (this.transaction.status === "pending" || this.transaction.status === "standby")) {
        this.startAutoTransferCountdown();
    }
  },

  beforeDestroy() {
    if (this.countDownInterval) {
      clearInterval(this.countDownInterval);
    }
  },

  methods: {
    getLocalDateTime(key) {
      try {
        const date = new Date(this.transactionKey(key));
        if (this.transactionKey(key)) {
          const localDateTime = convertUTCDateToLocalDate(date);
          return moment(localDateTime).format("yyyy-MM-DD HH:mm:ss");
        } else {
          return "N/A";
        }
      } catch (error) {
        return "N/A";
      }
    },
    suspicionInfo() {
      this.showSuspicionInfo = true;
    },
    transferCodeSent({ message }) {
      this.isSendingCode = false;
      Notify.success(message);
      this.$emit("reloadTransfer");
    },
    async resendTransferCode() {
      const id = this.transaction.payment.id;
      await Notify.confirm(`Do you want to resend Transfer OTP Code?`).then(() => {
        this.$http
          .post(`/api/v1/payments/${id}/resendCode`)
          .then((result) => {
            Notify.success(result.message);
            this.$emit("reloadTransfer");
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.error(error.message);
          });
      });
    },
    transactionKey(key, defaultValue = null) {
      return this.extractDataKey(this.transaction, key, defaultValue);
    },

    completePayment() {
      const id = this.transaction.payment.id;
      if (!id) {
        return;
      }
      Notify.confirm(`Marking this payment as complete`).then(() => {
        this.$http
          .post(`/api/v1/payments/complete/${id}`)
          .then(() => {
            Notify.success("Transaction has been completed successfully!");
            // this.$router.push({ name: "transfers.index" });
            window.location.reload();
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.errorFromResponse(error, { close: true });
          });
      });
    },
    toggleActivity() {
      const id = this.transaction.payment.id;
      if (!id) {
        return;
      }
      Notify.confirm(`Archieving the transaction will hide it from the list of transactions`).then(
        () => {
          this.isArchiving = true;
          this.$http
            .delete(`/api/v1/payments/${id}`)
            .then(() => {
              Notify.success("Transaction was archived successfully!");
              this.$router.push({ name: "transfers.index" });
            })
            // eslint-disable-next-line no-unused-vars
            .catch((error) => {
              this.archived = false;
              Notify.error("Transaction was not archived successfully. Please try again!");
            });
        },
      );
    },

    startTransfer() {
      this.completingTransfer = true;
      const method =
        this.transaction.payment.type === "card" ? "startCardTransfer" : "startBankTransfer";
      this.$http
        .post(`api/v1/payments/${this.transaction.payment.id}/${method}`)
        .then(({ message }) => {
          Notify.success(message);
          this.$router.push({ name: "transfers.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.completingTransfer = false));
    },
    standByTransfer() {
      this.standingByTransfer = true;
      this.$http
        .post(`api/v1/payments/${this.transaction.payment.id}/standBy`)
        .then(({ message }) => {
          Notify.success(message);
          this.$router.push({ name: "transfers.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.standingByTransfer = false));
    },
    pendingPaymentAndTransfer() {
      this.pendingPaymentTransfer = true;
      this.$http
        .post(`api/v1/payments/${this.transaction.payment.id}/pending`)
        .then(({ message }) => {
          Notify.success(message);
          this.$router.push({ name: "transfers.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.pendingPaymentTransfer = false));
    },
    retryTransfer() {
      this.isRetrying = true;
      this.$http
        .get(`api/v1/transactions/transfers/retry/${this.transaction.id}`)
        .then(({ message }) => {
          Notify.success(message);
          this.$router.push({ name: "transfers.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.isRetrying = false));
    },
    retrySuspiciousTransfer() {
      this.showBypassComment = true;
    },
    rejectTransfer() {
      this.showRejectionComment = true;
    },
    downloadInvoice() {
      this.processing = true;
      this.$http
        .get(`api/v1/payments/invoices/${this.transaction.payment.id}`, { responseType: "blob" })
        .then((response) => this.convertResponse(response))
        .catch((error) => Notify.errorFromResponse(error, { close: true }))
        .finally(() => {
          this.processing = false;
        });
    },
    convertResponse(response) {
      const blob = new Blob([response], { type: response.type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "invoice-" + this.transaction.id);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    restorePayment() {
      this.isRestoring = true;
      this.$http
        .get(`api/v1/payments/restore/${this.transaction.payment.id}`)
        .then(({ message }) => {
          Notify.success(message);
          this.$router.push({ name: "transfers.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.isRestoring = false));
    },
    bypassCommented({ message }) {
      Notify.success(message);
      this.$router.push({ name: "transfers.index" });
    },
    rejectCommented({ message }) {
      Notify.success(message);
      this.$router.push({ name: "transfers.index" });
    },

    startAutoTransferCountdown() {
      // Clear any existing interval
      if (this.countDownInterval) {
        clearInterval(this.countDownInterval);
      }

      // Initialize countdown
      this.countdownSecondsLeft = this.maxWaitMinutes * 60;
      this.updateCountdownDisplay();

      // Start countdown
      this.countDownInterval = setInterval(() => {
        this.countdownSecondsLeft--;
        this.updateCountdownDisplay();

        if (this.countdownSecondsLeft <= 0) {
          clearInterval(this.countDownInterval);
          this.handleCountdownCompletion();
        }
      }, 1000);
    },

    updateCountdownDisplay() {
      const minutes = Math.floor(this.countdownSecondsLeft / 60);
      const seconds = this.countdownSecondsLeft % 60;
      this.countdownDisplay = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },

    async handleCountdownCompletion() {
      try {
        if (this.transaction.status === 'pending' || this.transaction.status === 'standby') {
          
          await new Promise((resolve, reject) => {
            this.$emit('reloadTransfer', {
              resolve: (data) => {
                this.$set(this, 'transaction', data);
                console.log('Updated data.status:', data.status);
                if (data.status === 'pending' || data.status === 'standby') {
                  this.startTransfer();
                } else {
                  window.location.reload();
                }
                resolve();
              },
              reject
            });
          });
          
        }
      } catch (error) {
        console.error("Status check failed:", error);
        window.location.reload();
      }
    }

  },
};
</script>
